import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TierBadge, StatusBadge } from "@/components/Badges";
import { ArrowLeft, Coins, Mail, Phone, Cake, IdCard, CalendarDays, UserCheck, X, Award } from "lucide-react";
import { AwardBadgeDialog } from "@/components/admin/AwardBadgeDialog";
import { ChangeTierDialog } from "@/components/admin/ChangeTierDialog";
import { StripeSimulator } from "@/components/admin/StripeSimulator";
import { useAuth } from "@/context/AuthContext";
import { BadgeIcon } from "@/components/BadgeIcon";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { toast } from "sonner";
import { GrantPointsDialog } from "@/components/admin/GrantPointsDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum, fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";
import { PeriodsTable, INTERVAL, CHANNEL } from "@/components/admin/SubscriptionLedger";

const TABS = [
  { key: "puntos", label: "Movimientos de puntos" },
  { key: "canjes", label: "Canjes" },
  { key: "insignias", label: "Insignias" },
  { key: "nivel", label: "Historial de nivel" },
  { key: "suscripcion", label: "Suscripción" },
  { key: "decisiones", label: "Aprobación del registro" },
];
const TXN_TYPE: Record<string, string> = { earn: "Entrega", adjust: "Ajuste", redeem: "Canje", refund: "Reembolso", expire: "Retiro" };
const SUB_STATUS: Record<string, string> = { active: "Activa", trialing: "En prueba", past_due: "Pago atrasado", unpaid: "Impaga", incomplete: "Pago incompleto", canceled: "Cancelada", paused: "Pausada" };

// Ficha de cliente: todo lo necesario para atender una consulta, en una sola pantalla.
export default function AdminUserDetail() {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "puntos";
  const [granting, setGranting] = useState(false);
  const [badging, setBadging] = useState(false);
  const [changingTier, setChangingTier] = useState(false);
  const me = useAuth().user;
  const isSuperAdmin = me?.role === "admin";
  const canSimulate = (me?.permissions || []).includes("subscriptions");
  const [revoking, setRevoking] = useState<any>(null);
  usePageTitle(data?.user?.name ? `${data.user.name} · Clientes` : "Cliente");

  const load = () => {
    setFailed(false);
    api.get(`/admin/users/${id}`).then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(load, [id]); // eslint-disable-line

  if (failed) return <LoadError what="la ficha del cliente" onRetry={load} />;
  if (!data) return <PageLoader />;
  const u = data.user;
  const approval = u.approval_status || "approved";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-user-detail">
      <Link to="/admin/usuarios" className="inline-flex items-center gap-1 text-sm text-navy hover:underline">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver a Clientes
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">{u.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <TierBadge tier={u.tier} />
            <StatusBadge status={approval} />
            <StatusBadge status={u.status} />
          </div>
        </div>
        {approval === "approved" && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBadging(true)} className="rounded-full border-slate-200 text-slate-700" data-testid="detail-badge-button">
              <Award className="w-4 h-4 mr-1" aria-hidden="true" /> Entregar insignia
            </Button>
            <Button onClick={() => setGranting(true)} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="detail-grant-button">
              <Coins className="w-4 h-4 mr-1" aria-hidden="true" /> Entregar o ajustar puntos
            </Button>
          </div>
        )}
      </div>

      {u.status === "inactive" && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-800" role="status">
          Cuenta inhabilitada: no puede iniciar sesión.{u.inactive_reason ? ` Motivo: ${u.inactive_reason}` : ""} Puedes reactivarla desde Clientes, con el botón “Estado”.
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Fact label="Saldo de puntos" value={`${fmtNum(u.points)} pts`} strong />
        <Fact label="Nivel" value={<span className="inline-flex items-center gap-2">{u.tier}{u.tier_locked && <span className="text-xs font-sans font-medium bg-navy/10 text-navy rounded-full px-2 py-0.5">Fijado</span>}
            {isSuperAdmin && u.status !== "inactive" && <button type="button" onClick={() => setChangingTier(true)} className="text-xs font-sans text-navy underline" data-testid="detail-change-tier">Cambiar</button>}</span>}
          sub={`Lo conserva hasta el ${fmtDate(u.tier_valid_until + "T12:00:00")}. ${fmtNum(u.tier_points)} pts ganados este año (los canjes no los descuentan). ` + (u.next_tier ? `Le faltan ${fmtNum(u.points_to_next)} para ${u.next_tier}.` : "Está en el nivel más alto.")} />
        <Fact label="Plan" value={data.user.plan_name || (u.plan ? u.plan : "Armony Contigo (gratis)")} sub={u.subscription_status ? SUB_STATUS[u.subscription_status] || u.subscription_status : undefined} />
        <Fact label="Próximo canje" value={!u.redeem_status || u.redeem_status.can_redeem ? "Puede canjear ahora" : `Desde el ${u.redeem_status.next_redeem_label}`}
          sub={u.redeem_status?.last_redemption_label ? `Último canje: ${u.redeem_status.last_redemption_label} (uno cada ${u.redeem_status.cooldown_months} meses)` : "Aún no ha canjeado"} />
      </div>

      <div className="card-obsidian p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <Contact icon={IdCard} label="Cédula" value={u.cedula} />
        <Contact icon={Mail} label="Correo" value={u.email} />
        <Contact icon={Phone} label="Teléfono" value={u.phone} />
        <Contact icon={Cake} label="Cumpleaños" value={u.birthdate ? fmtDate(`${u.birthdate}T12:00:00`) : null} />
        <Contact icon={CalendarDays} label="Acepta comunicaciones" value={u.accept_comms ? "Sí" : "No"} />
        <Contact icon={CalendarDays} label="Cliente desde" value={fmtDate(u.created_at)} />
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Información del cliente">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setParams(t.key === "puntos" ? {} : { tab: t.key }, { replace: true })}
            data-testid={`detail-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${tab === t.key ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-600 hover:text-slate-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "puntos" && (data.transactions.length === 0 ? <EmptyState icon={Coins}>Este cliente aún no tiene movimientos de puntos.</EmptyState> : (
        <div className="card-obsidian overflow-hidden">
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Movimientos de puntos">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-4 py-3">Fecha</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Tipo</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Motivo</th>
                  <th scope="col" className="text-right font-medium px-4 py-3">Puntos</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Por</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t: any) => (
                  <tr key={t.id} className="border-b border-slate-200" data-testid="detail-txn-row">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                    <td className="px-4 py-3 text-slate-700">{TXN_TYPE[t.type] || t.type}</td>
                    <td className="px-4 py-3 text-slate-700">{t.reason || "—"}</td>
                    <td className={`px-4 py-3 text-right font-mono ${t.points < 0 ? "text-red-700" : "text-emerald-800"}`}>{t.points > 0 ? "+" : ""}{fmtNum(t.points)}</td>
                    <td className="px-4 py-3 text-slate-600">{t.created_by || (t.external_ref ? "App Armony Contigo" : t.mechanism === "carga_masiva" || t.type === "adjust" ? "Equipo (sin nombre registrado)" : t.mechanism?.startsWith("auto_") ? "Regla automática" : "Automático")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {tab === "insignias" && ((u.badges || []).length === 0 ? <EmptyState icon={Award}>Este cliente todavía no tiene insignias.</EmptyState> : (
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="detail-badges">
          {u.badges.map((b: any) => (
            <li key={b.id} className={`card-obsidian p-4 flex items-start gap-3 ${b.revoked_at ? "opacity-70" : ""}`}>
              <BadgeIcon icon={b.icon} muted={!!b.revoked_at} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900">{b.name}{b.points > 0 && <span className="text-xs font-semibold text-gold-ink"> · +{fmtNum(b.points)} pts</span>}</p>
                <p className="text-sm text-slate-700">{b.reason}</p>
                <p className="text-xs text-slate-600 mt-1">Entregada el {fmtDateTime(b.awarded_at)} por {b.awarded_by || "—"}</p>
                {b.revoked_at && <p className="text-xs text-red-700 mt-0.5">Retirada el {fmtDateTime(b.revoked_at)} por {b.revoked_by || "—"}{b.revoke_reason ? `: ${b.revoke_reason}` : ""}</p>}
              </div>
              {!b.revoked_at && <Button size="sm" variant="outline" onClick={() => setRevoking(b)} className="h-8 border-slate-200 text-slate-700">Retirar</Button>}
            </li>
          ))}
        </ul>
      ))}

      {tab === "nivel" && ((u.tier_changes || []).length === 0 ? <EmptyState>Todavía no hay cambios de nivel registrados.</EmptyState> : (
        <div className="card-obsidian overflow-hidden" data-testid="detail-tier-changes">
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Historial de nivel">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-4 py-3">Fecha</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Cambio</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Por qué</th>
                  <th scope="col" className="text-right font-medium px-4 py-3">Pts del año</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Por</th>
                </tr>
              </thead>
              <tbody>
                {u.tier_changes.map((c: any) => (
                  <tr key={c.id} className="border-b border-slate-200">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateTime(c.created_at)}</td>
                    <td className="px-4 py-3 text-slate-800">{c.from_tier || "—"} → <strong>{c.to_tier}</strong></td>
                    <td className="px-4 py-3 text-slate-700">{c.kind_label}{c.reason ? `: ${c.reason}` : ""}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtNum(c.qualifying_points)}</td>
                    <td className="px-4 py-3 text-slate-600">{c.actor || "Automático"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {tab === "canjes" && (data.redemptions.length === 0 ? <EmptyState>Este cliente no ha pedido premios.</EmptyState> : (
        <ul className="space-y-3">
          {data.redemptions.map((r: any) => (
            <li key={r.id} className="card-obsidian p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[12rem]">
                <p className="font-medium text-slate-900">{r.reward_name}</p>
                <p className="text-xs text-slate-600">{fmtDateTime(r.created_at)} · {fmtNum(r.points_spent)} pts{r.decision_reason ? ` · Motivo: ${r.decision_reason}` : ""}</p>
              </div>
              <StatusBadge status={r.status} />
            </li>
          ))}
        </ul>
      ))}

      {tab === "suscripcion" && (
        <div className="card-obsidian p-5 text-sm text-slate-700 space-y-1">
          {u.plan ? (<>
            <p><strong>Plan:</strong> {data.user.plan_name || u.plan}</p>
            <p><strong>Modalidad:</strong> {INTERVAL[u.plan_interval] || "—"}</p>
            <p><strong>Forma de pago:</strong> {CHANNEL[u.billing_channel] || (u.stripe_subscription_id ? "Tarjeta" : "—")}{u.auto_debit ? " · tiene débito automático con Armony" : ""}</p>
            <p><strong>Estado:</strong> {SUB_STATUS[u.subscription_status] || u.subscription_status || "—"}</p>
            <p><strong>Contratado el:</strong> {fmtDate(u.subscription_started_at)}</p>
            <p><strong>Precio contratado:</strong> {u.subscription_price != null ? `${fmtMoney(u.subscription_price, u.subscription_currency)} ${u.plan_interval === "year" ? "al año" : "al mes"}` : "—"}</p>
            <p><strong>Periodo en curso:</strong> {fmtDate(u.subscription_current_period_start)} – {fmtDate(u.subscription_current_period_end)}</p>
            {u.subscription_next_plan && <p><strong>Cambio programado:</strong> desde el {fmtDate(u.subscription_current_period_end)} pasa a {u.next_plan_name || u.subscription_next_plan} · {INTERVAL[u.subscription_next_interval] || "—"} por {fmtMoney(u.subscription_next_price)}</p>}
            <p><strong>{u.subscription_cancel_at ? "Termina (cancelación programada):" : "Renueva o vence:"}</strong> {fmtDate(u.subscription_cancel_at || u.subscription_current_period_end)}</p>
          </>) : <p>Este cliente está en Armony Contigo, el plan gratuito: no contrató Armony Contigo Plus ni Premium.</p>}
        </div>
      )}

      {tab === "suscripcion" && u.stripe_simulated && canSimulate && u.plan && u.billing_channel === "stripe" && (
        <StripeSimulator userId={u.id} onDone={load} />
      )}

      {tab === "suscripcion" && (u.cancellations || []).length > 0 && (
        <div className="card-obsidian overflow-hidden" data-testid="detail-cancellations">
          <h3 className="px-5 pt-4 text-sm font-semibold text-slate-800">Historial de cancelaciones</h3>
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Historial de cancelaciones">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Pedida el</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Termina</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {u.cancellations.map((c: any) => (
                  <tr key={c.id} className="border-b border-slate-200 last:border-0">
                    <td className="px-5 py-3 text-slate-700">{fmtDateTime(c.requested_at)}</td>
                    <td className="px-5 py-3 text-slate-700">{fmtDate(c.effective_at)}{c.mode === "inmediata" ? " (inmediata: pago atrasado)" : ""}</td>
                    <td className="px-5 py-3 text-slate-700">{{ programada: "Programada", revertida: `Deshecha el ${fmtDate(c.reverted_at)}`, efectiva: "Efectiva" }[c.status as string] || c.status}</td>
                    <td className="px-5 py-3 text-slate-700">{[c.reason_label, c.reason_text].filter(Boolean).join(": ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "suscripcion" && (u.subscription_changes || []).length > 0 && (
        <div className="card-obsidian overflow-hidden" data-testid="detail-plan-changes">
          <h3 className="px-5 pt-4 text-sm font-semibold text-slate-800">Historial del plan</h3>
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Historial del plan">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Desde el</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Movimiento</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Plan</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Precio</th>
                </tr>
              </thead>
              <tbody>
                {u.subscription_changes.map((c: any) => {
                  const planLabel = (name: string, iv: string) => (name ? `${name} · ${INTERVAL[iv] || "—"}` : "—");
                  return (
                    <tr key={c.id} className="border-b border-slate-200 last:border-0">
                      <td className="px-5 py-3 text-slate-700">{fmtDate(c.effective_at)}</td>
                      <td className="px-5 py-3 text-slate-700">{{ alta: "Contratación", cambio_plan: "Cambio de plan", fin: "Fin del plan" }[c.kind as string] || c.kind}</td>
                      <td className="px-5 py-3 text-slate-700">
                        {c.kind === "alta" ? planLabel(c.to_plan_name, c.to_interval)
                          : c.kind === "fin" ? planLabel(c.from_plan_name, c.from_interval)
                          : <>{planLabel(c.from_plan_name, c.from_interval)} → {planLabel(c.to_plan_name, c.to_interval)}</>}
                      </td>
                      <td className="px-5 py-3 text-right font-mono">{c.unit_price != null ? fmtMoney(c.unit_price, c.currency) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "suscripcion" && (u.subscription_periods || []).length > 0 && (
        <div className="card-obsidian overflow-hidden" data-testid="detail-periods">
          <h3 className="px-5 pt-4 text-sm font-semibold text-slate-800">Periodos cobrados</h3>
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Periodos cobrados">
            <PeriodsTable rows={u.subscription_periods} />
          </div>
        </div>
      )}

      {tab === "decisiones" && ((u.approval_history || []).length === 0 ? <EmptyState>No hay decisiones registradas sobre este registro.</EmptyState> : (
        <ul className="space-y-3">
          {[...u.approval_history].reverse().map((h: any, i: number) => (
            <li key={i} className="flex items-start gap-3 p-4 rounded-lg card-obsidian">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${h.decision === "approved" ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
                {h.decision === "approved" ? <UserCheck className="w-4 h-4 text-emerald-700" aria-hidden="true" /> : <X className="w-4 h-4 text-red-700" aria-hidden="true" />}
              </div>
              <div>
                <p className="text-sm text-slate-800">{h.decision === "approved" ? "Aprobado" : "Rechazado"} por <strong>{h.by || "—"}</strong></p>
                <p className="text-xs text-slate-600">{fmtDateTime(h.at)}</p>
                {h.reason && <p className="text-xs text-slate-700 mt-1">Motivo: {h.reason}</p>}
              </div>
            </li>
          ))}
        </ul>
      ))}

      <GrantPointsDialog user={granting ? u : null} onClose={() => setGranting(false)} onDone={load} />
      <ChangeTierDialog user={changingTier ? u : null} onClose={() => setChangingTier(false)} onDone={() => { load(); setParams({ tab: "nivel" }, { replace: true }); }} />
      <AwardBadgeDialog user={badging ? u : null} owned={(u.badges || []).filter((b: any) => !b.revoked_at).map((b: any) => b.badge_id)}
        onClose={() => setBadging(false)} onDone={() => { load(); setParams({ tab: "insignias" }, { replace: true }); }} />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        danger
        title={`¿Retirar la insignia “${revoking?.name || ""}”?`}
        description={<>
          <p>{u.name} dejará de verla en su portal. Queda registrado quién la retiró y cuándo.</p>
          {revoking?.points > 0 && <p>Los {fmtNum(revoking.points)} puntos que recibió con ella no se descuentan. Si corresponde, haz un ajuste de puntos.</p>}
        </>}
        confirmLabel="Retirar insignia"
        onConfirm={async () => {
          try { await api.post(`/admin/users/${u.id}/badges/${revoking.id}/revoke`, {}); toast.success("Insignia retirada"); load(); }
          catch (e) { toast.error(apiError(e)); throw e; }
        }}
      />
    </div>
  );
}

const Fact = ({ label, value, sub, strong }: { label: string; value: React.ReactNode; sub?: string; strong?: boolean }) => (
  <div className="card-obsidian p-5">
    <p className="text-sm text-slate-600">{label}</p>
    <p className={`mt-1 ${strong ? "text-2xl font-serif" : "text-lg font-medium"} text-slate-900`}>{value}</p>
    {sub && <p className="text-xs text-slate-600 mt-1">{sub}</p>}
  </div>
);

const Contact = ({ icon: Icon, label, value }: { icon: any; label: string; value?: string | null }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5" aria-hidden="true" />
    <div><p className="text-xs text-slate-600">{label}</p><p className="text-slate-900 break-all">{value || "—"}</p></div>
  </div>
);
